/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

// Startup point for the client side application

/* eslint-disable no-unused-vars */
import 'babel-polyfill';
import 'core-js/modules/es6.promise';
import 'core-js/modules/es6.array.iterator';
import '../utils/Globals';
import {loadPolyfill} from '../utils/Polyfill';
if (
	'Map' in window
	&& 'forEach' in NodeList.prototype
	&& 'startsWith' in String.prototype
	&& 'endsWith' in String.prototype
	&& 'includes' in String.prototype
	&& 'includes' in Array.prototype
	&& 'assign' in Object
	&& 'entries' in Object
	&& 'keys' in Object
) {
	loadPolyfill();
}

const startLoadingTime = performance.now();
let loadingIntervalCount = 0;
const loadingInterval = setInterval(() => {
	window.cchome.util.logger.log('Client', 'Loading interval', (loadingIntervalCount += 5));
}, 5000);
window.cchome.util.setQueryParams();
import https from 'https';
const language = window.cchome.util.getStartupLanguage();
import AnalyticsUtil from '../utils/AnalyticsUtil';
import GeoService from './services/GeoService';
import LocalizationService from './services/LocalizationService';
// eslint-disable-next-line no-duplicate-imports
import {Utils} from '../utils/Globals';
import StoreUtils from '../utils/StoreUtils';
import {CCHomeAppsAppPickerUtil, CCHomeAppsUtil, singleAppPlans, productIdMap} from 'cchome-apps';
let IntlProvider: any = {};
let LoadTranslations: any = {};

function sendRedirectAnalytic(url, reason, errorDesc?) {
	const analytics = new AnalyticsUtil(true);
	return analytics.sendRedirectAnalytic('redirect', url, reason, errorDesc);
}

function redirectCChomeUser(ingestPromise, redirectURL) {
	const timedOutRedirect = setTimeout(() => {
		if (redirectURL !== 'loggedOut') {
			window.location.href = redirectURL;
		}
		else {
			RedirectionUtils.redirectLoggedOut(language);
		}
	}, 2000);

	/* istanbul ignore else */
	if (ingestPromise && ingestPromise.then) {
		ingestPromise.then(() => {
			clearTimeout(timedOutRedirect);
			if (redirectURL !== 'loggedOut') {
				window.location.href = redirectURL;
			}
			else {
				RedirectionUtils.redirectLoggedOut(language);
			}
		});
	}
}

if (!window.cchome.util.storageAvailable('cookie')) {
	const redirectURL = `${window.location.origin}/errors/cookieDisabled.html?ref=${encodeURIComponent(
		window.location.href,
	)}`;
	const ingestPromise = sendRedirectAnalytic(redirectURL, 'cookie-disabled');
	redirectCChomeUser(ingestPromise, redirectURL);
}
else if (window.cchome.util.isUnsupportedBrowser()) {
	const redirectURL = `${window.location.origin}/errors/unsupportedBrowser.html`;
	const ingestPromise = sendRedirectAnalytic(redirectURL, 'unsupported-browser');
	redirectCChomeUser(ingestPromise, redirectURL);
}
else {
	LoadTranslations = LocalizationService.loadTranslations(language);
	IntlProvider = require('react-intl').IntlProvider;
}

import React, {Suspense} from 'react';
import {BrowserRouter} from 'react-router-dom';
import {Provider} from 'react-redux';
import reducers from './reducers';
import TransitionScreen from './containers/TransitionScreen/TransitionScreen';
import ErrorBoundary from './containers/ErrorBoundary/ErrorBoundary';
import SpectrumProvider from '@react/react-spectrum/Provider';
import {getEnvironment} from '../utils/Environment';

/* eslint-enable no-unused-vars */

import axios from 'axios';
import thunk from 'redux-thunk';
import ReactDOM from 'react-dom';
import {createStore, applyMiddleware, compose} from 'redux';
import {renderRoutes} from 'react-router-config';
import {
	FETCH_CURRENT_USER,
	FETCH_USER_ENTITLEMENT,
	SET_BOOTSTRAP_COMPLETED,
	SET_LANGUAGE,
	SET_THOR_PRODUCT_LIST,
} from './types';
import getConfig from '../config';
import LearnTrackingService from './services/LearnTrackingService';
import BootstrapService from './services/BootstrapService';
import EventsService from './services/EventsService';
// import ARSService from './services/ARSService';
import ThorUtils from '../utils/ThorUtils';
import RedirectionUtils from '../utils/RedirectionUtils';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
// eslint-disable-next-line no-duplicate-imports
import GlobalsUtils from '../utils/Globals';

window.ENV = getEnvironment();
RedirectionUtils.setEnteringURL();

// create an axios instance with base URL '/api'
const axiosInstance = axios.create({
	baseURL: '/api',
});

const rootEl = document.querySelector('#root');
const loaderEl = document.querySelector('#loader');

/* istanbul ignore next */
if (!(rootEl instanceof Element) || !(loaderEl instanceof Element)) {
	throw new Error('invalid type');
}

let composeEnhancers = compose;

/* istanbul ignore else */
if (typeof getEnvironment() === 'string' && getEnvironment() !== 'prod') {
	composeEnhancers
		= typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
			? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
				// Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
				features: {
					pause: true, // start/pause recording of dispatched actions
					lock: false, // lock/unlock dispatching actions and side effects
					persist: true, // persist states on page reloading
					export: true, // export history of actions in a file
					import: false, // import history of actions from a file
					jump: true, // jump back and forth (time travelling)
					skip: true, // skip (cancel) actions
					reorder: false, // drag and drop actions in the history list
					dispatch: true, // dispatch custom actions or action creators
					test: false, // generate tests for the selected actions
				},
				// other options like actionSanitizer, stateSanitizer
				// More information:- https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/API/Arguments.md#options
			})
			: compose;
}

const enhancer = composeEnhancers(applyMiddleware(thunk.withExtraArgument(axiosInstance)));

// create redux store with redux thunk
export const store = createStore(reducers, {}, enhancer);
StoreUtils.store = store;

/* istanbul ignore next */
function progressCallback() {
	/* progress*/
	/* show progress*/
}

async function fetchGeoInfo() {
	let geoData = {
		country: '',
		'Accept-Language': '',
	};
	const geoservice = new GeoService();

	try {
		// This doesn't stop the UI from loading
		geoData = await geoservice.fetchGeoData();
	}
	catch (e) {
		// Error is handled in the GeoService
		// Set the default value
	}

	window.cchome.util.geo = geoData;

	window.cchome.util.logger.log('Home', 'Logged CCHome Geo Data');
}

// dynamically load the Adobe Launch script
function loadAdobeLaunch() {
	if (!window.cchome.util.hasFeature('disable_launch')) {
		let notice;
		// marketingtech is the adobe launch flag to check if we are loading the
		// GDPR script.
		let environment = 'production';

		switch (getEnvironment()) {
			case 'prod':
				environment = 'production';
				break;

			case 'stage':
				environment = 'stage';
				break;

			case 'local':
			case 'dev':
				environment = 'dev';
				break;

			default:
				environment = 'production';
				break;
		}

		const script: any = document.createElement('script');
		script.src = 'https://www.adobe.com/etc/beagle/public/globalnav/adobe-privacy/latest/privacy.min.js';

		script.async = true;
		document.body ? document.body.appendChild(script) : '';

		window.marketingtech.adobe = {
			audienceManager: true,
			launch: {
				property: 'global',
				environment,
			},
		};

		if (window.cchome.util.hasFeature('recommendation_cards')) {
			window.marketingtech.adobe.target = true;
		}

		if (CCHomeAppsAppPickerUtil.isNUJAllAppsExperience() || CCHomeAppsAppPickerUtil.isSingleAppPlanNUJExp()) {
			window.marketingtech.integrate = {
				...window.marketingtech.integrate,
				contentSquare: true,
			};
		}
		// require the adobe launch library
		require('@marketingtech/launch-code/dist/main.min.js');

		window.fedsConfig = window.fedsConfig || {};
		window.fedsConfig.privacy = window.fedsConfig.privacy || {};
		window.fedsConfig.privacy.otDomainId = '7a5eb705-95ed-4cc4-a11d-0cc5760e93db';
		window.fedsConfig.privacy.footerLinkSelector = '.footer-container .consent-link-hidden';
	}
}
window.marketingtech = window.marketingtech || {};
loadAdobeLaunch();

// dynamically load the UCV3 and I2T Preload script
function loadUCV3PreloadScript() {
	if (window.cchome.util.hasFeature('temp_cch_12205') || window.cchome.util.hasFeature('temp_cch_12163')) {
		const commerceDomain
			= getEnvironment() === 'prod' ? 'https://commerce.adobe.com' : 'https://commerce-stg.adobe.com';
		const scriptSrc = `${commerceDomain}/checkout/iframe/uc-preload.js`;

		const script: any = document.createElement('script');
		script.src = scriptSrc;
		script.id = 'uc-preload-script';
		script.type = 'text/javascript';
		script.defer = true;
		script.async = true;
		document.body ? document.body.appendChild(script) : '';
	}
}

store.dispatch({
	type: SET_LANGUAGE,
	langCookie: language,
});

const typekitId = getConfig('SUPPORTED_LANGUAGES')[language].typekitId;

// Initialize the Proxy Frame for uploading files in App Homes
if (!GlobalsUtils.isIE()) {
	GlobalsUtils.getProxyFrame();
	window.cchome.util.logger.log('Client Bootstrap Service', 'Proxy Frame Loaded');
}

if (LoadTranslations.then) {
	LoadTranslations.then((localizedStrings) => {
		// Show the transition screen. On the first visit, this should show for a minimum of 5 seconds.
		// For repeat visits, this should dismiss as soon as the Bootstrap process completes.
		Utils.localizedStrings = localizedStrings;
		ReactDOM.render(
			<Provider store={store}>
				{/* $FlowFixMe */}
				<IntlProvider locale={language} messages={localizedStrings}>
					<SpectrumProvider theme='light' typekitId={typekitId}>
						<TransitionScreen />
					</SpectrumProvider>
				</IntlProvider>
			</Provider>,
			loaderEl,
		);
		BootstrapService(progressCallback, store) // eslint-disable-line new-cap
			.then((initialState: any) => {
				// libraries pages behind feature flag
				if (!window.cchome.util.hasFeature('temp_enable_libraries')) {
					const pathNames = window.location.pathname.split('/');
					if (
						pathNames.length >= 3
						&& pathNames[1].toLowerCase() === 'cc'
						&& pathNames[2].toLowerCase() === 'libraries'
					) {
						window.location.href = window.location.origin;
					}
				}

				// Redirection for CC Home Apps Urls in case of productId changes
				if (CCHomeAppsUtil.getRedirectToNewProductIdUrl()) {
					window.location.pathname = CCHomeAppsUtil.getRedirectToNewProductIdUrl();
				}

				// Bootstrap the application as soon as the services are ready.

				/* istanbul ignore else */
				if (initialState) {
					store.dispatch({
						type: SET_BOOTSTRAP_COMPLETED,
						payload: true,
					});
					store.dispatch({
						type: FETCH_CURRENT_USER,
						payload: initialState,
					});
					store.dispatch({
						type: FETCH_USER_ENTITLEMENT,
						payload: initialState.isUserEntitled,
					});

					window.cchome.util.logger.log('Client Bootstrap Service', 'Initial State', initialState);

					if (initialState.isUserEntitled) {
						window.trackingService = new LearnTrackingService();

						// make the EventsService available globally
						// this is to enable the reporting of events from cchome-learn

						/* istanbul ignore else */
						if (window.cchome.util.hasFeature('temp_enable_intents_server_tracking')) {
							window.eventsService = new EventsService();
						}

						// THOR detection
						window.cchome.util.hasFeature('temp_enable_lib_open_in_app') && ThorUtils.initializeWAMClient();

						// ARS call
						// new ARSService().fetchInstalledApps()
						// 	.then(data => window.cchome.util.logger.info('client', 'ARSService App Installed', data));
					}

					// Fetch the Geo data for the logger
					fetchGeoInfo();

					loadUCV3PreloadScript();

					window.cchome.analyticsUtils = new AnalyticsUtil();

					/* Remove analytics for referrer each time we launch */
					AnalyticsUtil.removeReferrer();

					ReactDOM.render(
						<Provider store={store}>
							{/* $FlowFixMe */}
							<IntlProvider locale={language} messages={localizedStrings}>
								<ErrorBoundary locale={language}>
									<BrowserRouter>
										<SpectrumProvider theme='light' typekitId={typekitId}>
											<Suspense fallback={<TransitionScreen />}>
												{renderRoutes(require('./Routes/MainRoutes').default, {
													app: store.getState().app,
													profile: store.getState().profile,
												})}
											</Suspense>
										</SpectrumProvider>
									</BrowserRouter>
								</ErrorBoundary>
							</IntlProvider>
						</Provider>,
						rootEl,
					);
					window.cchome.util.logger.info(
						'Home',
						'Loaded CCHome Successfully',
						performance.now() - startLoadingTime,
					);
					clearInterval(loadingInterval);
				}
			})
			.catch((e) => {
				// redirect to Logged out
				clearInterval(loadingInterval);

				const hostname = location && location.hostname;
				let errorStack;
				if (getEnvironment() === 'dev' && hostname !== 'dev.hollywoodstudios.corp.adobe.com') {
					errorStack = e && e.stack;
				}
				window.cchome.util.logger.error('Client', 'Error loading application', {
					errorMessage: e.toString(),
					performanceTime: performance.now() - startLoadingTime,
					errorStack,
				});

				const ingestPromise = sendRedirectAnalytic(window.location.origin, 'bootstrap-failed', e);
				redirectCChomeUser(ingestPromise, 'loggedOut');
				store.dispatch({
					type: SET_BOOTSTRAP_COMPLETED,
					payload: true,
				});
			});
	}).catch((e) => {
		// redirect to 5xx page
		clearInterval(loadingInterval);
		window.cchome.util.logger.error('Client', 'Error loading application', {
			errorMessage: e.toString(),
			performanceTime: performance.now() - startLoadingTime,
		});
		const currentURL = window.location.origin;
		const rediectURL = `${currentURL}/errors/5XX.html`;
		const ingestPromise = sendRedirectAnalytic(rediectURL, 'load-translations-failed', e);
		redirectCChomeUser(ingestPromise, rediectURL);
		store.dispatch({
			type: SET_BOOTSTRAP_COMPLETED,
			payload: true,
		});
	});
}
